const DEFAULT_BREAKPOINT = 750;
// How far out a film starts downloading. At 200px a video had about a fifth of a second of
// warning before it was on screen, which is not enough to fetch anything, so it arrived late
// and visibly. The still underneath now covers the gap, and this gives the download a head
// start measured in seconds of scrolling rather than pixels. It is a trade against data spent
// on films that are scrolled past -- a reason to raise it only as far as it needs to go.
const NEAR_VIEWPORT_MARGIN = '800px';
const READY_CLASS = 'is-ready';
// A hero's film is on screen before anything has been downloaded, so its 6.9MB starts beside
// the 132KB still that LCP is measured on and holds that frame back. Callers that sit above
// the fold mark the element and the first request waits for the load event -- see the note in
// snippets/kl-video.liquid. There is no timer behind this on purpose: if load never fires the
// still simply stays, which is the same picture without the motion.
const DEFER_UNTIL_LOAD_ATTRIBUTE = 'data-kl-video-defer-until-load';
const activeVideos = new Map();

function runWhenPageHasLoaded(runTask) {
  if (document.readyState === 'complete') {
    runTask();
    return;
  }

  window.addEventListener('load', runTask, { once: true });
}

function startListening(mediaQuery, handleChange) {
  if (typeof mediaQuery.addEventListener === 'function') {
    mediaQuery.addEventListener('change', handleChange);
    return;
  }

  if (typeof mediaQuery.addListener === 'function') mediaQuery.addListener(handleChange);
}

function stopListening(mediaQuery, handleChange) {
  if (typeof mediaQuery.removeEventListener === 'function') {
    mediaQuery.removeEventListener('change', handleChange);
    return;
  }

  if (typeof mediaQuery.removeListener === 'function') mediaQuery.removeListener(handleChange);
}

function getSourcesForDevice(sourceElements, deviceName) {
  return sourceElements.filter((sourceElement) => {
    const device = sourceElement.dataset.klVideoDevice;

    return device === deviceName || device === 'both';
  });
}

function getSourceSignature(sourceElements) {
  return sourceElements.map((sourceElement) => sourceElement.dataset.klVideoSrc).join('|');
}

function initVideo(videoElement) {
  if (activeVideos.has(videoElement)) return;

  const sourceElements = Array.from(videoElement.querySelectorAll('[data-kl-video-src]'));

  if (!sourceElements.length) return;

  const breakpoint = Number.parseInt(videoElement.dataset.klVideoBreakpoint, 10) || DEFAULT_BREAKPOINT;
  const desktopQuery = window.matchMedia('(min-width: ' + breakpoint + 'px)');
  const reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

  let isNearViewport = false;
  let isWaitingForPageLoad = videoElement.hasAttribute(DEFER_UNTIL_LOAD_ATTRIBUTE);
  let loadedSignature = null;

  function requestPlayback() {
    videoElement.muted = true;

    const playRequest = videoElement.play();

    if (!playRequest || typeof playRequest.catch !== 'function') return;

    playRequest.catch((error) => {
      if (error.name === 'AbortError') return;

      console.debug('kl-video: the browser refused to play this video', {
        source: videoElement.currentSrc,
        error,
      });
    });
  }

  function applySourcesForCurrentWidth() {
    const deviceName = desktopQuery.matches ? 'desktop' : 'mobile';
    const wantedSources = getSourcesForDevice(sourceElements, deviceName);

    if (!wantedSources.length) {
      loadedSignature = null;
      videoElement.pause();
      videoElement.classList.remove(READY_CLASS);
      return false;
    }

    const signature = getSourceSignature(wantedSources);

    if (signature === loadedSignature) return true;

    loadedSignature = signature;

    sourceElements.forEach((sourceElement) => {
      if (wantedSources.indexOf(sourceElement) === -1) {
        sourceElement.removeAttribute('src');
        return;
      }

      sourceElement.setAttribute('src', sourceElement.dataset.klVideoSrc);
    });

    videoElement.load();

    return true;
  }

  function teardown() {
    if (observer) observer.disconnect();

    stopListening(desktopQuery, handleQueryChange);
    stopListening(reducedMotionQuery, handleQueryChange);
    activeVideos.delete(videoElement);
  }

  function update() {
    if (!videoElement.isConnected) {
      teardown();
      return;
    }

    if (isWaitingForPageLoad) return;

    if (!isNearViewport) return;

    if (reducedMotionQuery.matches) {
      videoElement.pause();
      videoElement.classList.remove(READY_CLASS);
      return;
    }

    if (!applySourcesForCurrentWidth()) return;

    if (!videoElement.paused) return;

    requestPlayback();
  }

  function handleQueryChange() {
    update();
  }

  function handleIntersection(entries) {
    entries.forEach((entry) => {
      isNearViewport = entry.isIntersecting;

      if (entry.isIntersecting) {
        update();
        return;
      }

      videoElement.pause();
    });
  }

  const observer =
    typeof IntersectionObserver === 'function'
      ? new IntersectionObserver(handleIntersection, { rootMargin: NEAR_VIEWPORT_MARGIN })
      : null;

  function handleReady() {
    videoElement.classList.add(READY_CLASS);
  }

  function handleFailure(event) {
    videoElement.classList.remove(READY_CLASS);

    console.debug('kl-video: this video could not be played, the image below it stands in', {
      source: (event.target && event.target.src) || videoElement.currentSrc,
    });
  }

  videoElement.addEventListener('playing', handleReady);

  videoElement.addEventListener('error', handleFailure, true);

  activeVideos.set(videoElement, teardown);

  startListening(desktopQuery, handleQueryChange);
  startListening(reducedMotionQuery, handleQueryChange);

  if (isWaitingForPageLoad) {
    runWhenPageHasLoaded(() => {
      isWaitingForPageLoad = false;
      update();
    });
  }

  if (!observer) {
    isNearViewport = true;
    update();
    return;
  }

  observer.observe(videoElement);
}

function releaseDetachedVideos() {
  activeVideos.forEach((teardown, videoElement) => {
    if (videoElement.isConnected) return;

    teardown();
  });
}

function initVideos(rootElement) {
  releaseDetachedVideos();
  rootElement.querySelectorAll('[data-kl-video]').forEach(initVideo);
}

initVideos(document);

document.addEventListener('shopify:section:load', (event) => {
  initVideos(event.target);
});

document.addEventListener('shopify:section:unload', releaseDetachedVideos);
